import SEO, { getBreadcrumbSchema } from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const RentersFAQ = () => {
  const faqs = [
    {
      question: "How do I apply for a rental property?",
      answer: "All applications are submitted online through our tenant portal. You'll need to provide: photo ID, proof of income (recent pay stubs), employment verification, previous landlord references, and a $50 application fee (per adult). We respond to all complete applications within 24-48 hours."
    },
    {
      question: "What are the rental requirements?",
      answer: "Applicants must have: minimum credit score of 600, gross monthly income of at least 3x the rent, positive rental history with no evictions in the past 5 years, and pass criminal background check. We review each application individually and consider the complete picture."
    },
    {
      question: "How much is the security deposit?",
      answer: "Security deposits are typically equal to one month's rent for properties in Overland Park, Leawood, Lenexa, and Olathe. For approved pets, there's an additional pet deposit of $250-$500 per pet depending on size and breed. All deposits are held in accordance with Kansas law."
    },
    {
      question: "Are pets allowed?",
      answer: "Pet policies vary by property. Many of our Kansas rentals are pet-friendly with breed and size restrictions. Pet rent is typically $25-$50 per month per pet, plus the pet deposit. Service animals are always welcome in compliance with fair housing laws."
    },
    {
      question: "How do I pay rent?",
      answer: "Rent is due on the 1st of each month and can be paid online through our tenant portal via ACH bank transfer (free) or credit/debit card (3% convenience fee). Rent is considered late after the 5th with a $75 late fee."
    },
    {
      question: "How do I submit a maintenance request?",
      answer: "Log into your tenant portal and submit a maintenance request 24/7. For emergencies (no heat, water leaks, lockouts), call our emergency hotline. Non-emergency requests are typically addressed within 48 hours. We use vetted, licensed contractors for all repairs."
    },
    {
      question: "Can I make changes to the property?",
      answer: "Minor changes like paint color or hanging pictures are usually fine (with owner approval). Major modifications require written consent from the property owner. Any unauthorized alterations may result in charges to restore the property to its original condition."
    },
    {
      question: "What utilities am I responsible for?",
      answer: "Tenants typically pay for electric, gas, water/sewer, and internet. Specific utility responsibilities are outlined in your lease agreement. In Johnson County, average monthly utilities run $150-$250 depending on property size and season."
    },
    {
      question: "What is the lease term?",
      answer: "Standard leases are 12 months. We typically offer lease renewal options 60 days before expiration. Month-to-month options may be available after the initial lease term with owner approval, usually at a slightly higher rate."
    },
    {
      question: "What happens when I move out?",
      answer: "Provide 30 days written notice before your lease end date. Schedule a move-out inspection with us. Clean the property thoroughly, repair any damage beyond normal wear and tear, and return all keys. Your security deposit (minus any deductions) is returned within 30 days per Kansas law, with an itemized statement of any deductions."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Renter FAQ | Rently Properties Kansas City Rentals"
        description="Common renter questions for Kansas City metro rentals. Learn about applications, deposits, pets, rent payment, and maintenance in Overland Park, Leawood, Lenexa & Olathe KS."
        canonical="/renters-faq"
        keywords="renter FAQ Kansas City, rental application process, security deposit Kansas, pet policy rental, rent payment online, maintenance request, Johnson County rentals"
        schema={[
          faqSchema,
          getBreadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Renter FAQ", path: "/renters-faq" }
          ])
        ]}
      />
      <Header />

      <main className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Renter <span className="text-primary">FAQ</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about renting in Johnson County, Kansas
            </p>
          </div>

          <Card>
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-6">
              Ready to find your next home in Kansas City metro?
            </p>
            <Button size="lg" className="bg-secondary hover:bg-secondary/90" asChild>
              <Link to="/#contact">View Available Rentals</Link>
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default RentersFAQ;
